// ============================================================================
// AUDIT DOMAIN API CLIENT
// ============================================================================
// Domain-specific API for audit logs, statistics, and compliance tracking
// ============================================================================

import { ApiClient } from '../../core/apiClient';
import type { AuditLog } from '../../types';

export interface AuditFinding {
  id: number;
  compliance_page_id: number;
  framework_mapping_id?: number;
  auditor_id: number;
  company_id: number;
  finding_type: 'non_conformity' | 'observation' | 'opportunity_for_improvement' | 'positive_practice';
  severity: 'critical' | 'major' | 'minor';
  title: string;
  description: string;
  evidence_reference?: string;
  corrective_action?: string;
  target_completion_date?: string;
  status: 'open' | 'in_progress' | 'closed' | 'verified';
  created_at: string;
  updated_at: string;
  verified_at?: string;
  verified_by?: number;
  deleted_at?: string;
}

export interface AuditorCompany {
  id: number;
  name: string;
  legal_name: string;
  industry: string;
  assigned_at: string;
  frameworks_count: number;
  compliance_score: number;
}

export interface FrameworkReview {
  id: number;
  framework_id: number;
  framework_version_id: number;
  company_id: number;
  company_name: string;
  framework_name: string;
  status: 'pending' | 'in_progress' | 'completed' | 'overdue';
  compliance_score: number;
  total_requirements: number;
  completed_requirements: number;
  critical_findings: number;
  major_findings: number;
  minor_findings: number;
  last_review_date?: string;
  next_review_date?: string;
  assigned_at: string;
}

export interface EvidenceReview {
  id: number;
  compliance_page_id: number;
  company_id: number;
  company_name: string;
  framework_name: string;
  requirement_title: string;
  submitted_by: number;
  submitted_by_name: string;
  submitted_date: string;
  evidence_type: 'document' | 'text' | 'mixed';
  file_name?: string;
  file_url?: string;
  status: 'pending_review' | 'submitted' | 'approved' | 'rejected' | 'INTERNAL_VERIFIED' | 'REJECTED';
  description?: string;
  notes?: string;
  ocr_confidence_score?: number;
  // Reviewer audit trail — populated when status is INTERNAL_VERIFIED / REJECTED
  reviewed_by?: number;
  reviewed_at?: string;
  reviewer_name?: string;
}

export interface AssessmentSummary {
  id: number;
  company_id: number;
  company_name: string;
  framework_id: number;
  framework_name: string;
  assessment_period: string;
  overall_score: number;
  status: 'draft' | 'in_progress' | 'completed' | 'overdue';
  completed_requirements: number;
  total_requirements: number;
  critical_findings: number;
  major_findings: number;
  minor_findings: number;
  positive_practices: number;
  created_at: string;
  updated_at: string;
}

export interface AuditLogFilters {
  userId?: number;
  resource?: string;
  resourceId?: string;
  action?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface AuditStatistics {
  dailyStats: Array<{
    total_logs: number;
    unique_users: number;
    unique_resources: number;
    log_date: string;
  }>;
  topActions: Array<{
    action: string;
    count: number;
  }>;
  topResources: Array<{
    entity_type: string;
    count: number;
  }>;
}

export interface AuditExportJob {
  id: string;
  user_id: number;
  status: 'pending' | 'running' | 'completed' | 'failed';
  filters: string;
  format: 'csv' | 'json';
  file_path?: string;
  file_size?: number;
  record_count?: number;
  error_message?: string;
  started_at?: string;
  completed_at?: string;
  created_at: string;
}

export interface RetentionPolicy {
  entity_type: string;
  retention_days: number;
  description?: string;
  created_at?: string;
  updated_at?: string;
}

export interface AuditCleanupJob {
  id: string;
  entity_type: string;
  retention_days: number;
  status: 'pending' | 'running' | 'completed' | 'failed';
  records_archived?: number;
  error_message?: string;
  started_at?: string;
  completed_at?: string;
  created_at: string;
}

// ============================================================================
// AUDIT API CLIENT CLASS
// ============================================================================

class AuditApiClient extends ApiClient {
  constructor() {
    super({
      enableLogging: import.meta.env.DEV,
    });
  }

  // ============================================
  // AUDIT LOGS API
  // ============================================

  /**
   * Get audit logs with filters and pagination
   */
  async getAuditLogs(filters?: AuditLogFilters): Promise<AuditLog[]> {
    const response = await this.client.get('/audit/logs', { params: filters });
    return response.data.logs || [];
  }

  /**
   * Get current user's audit logs
   */
  async getMyAuditLogs(limit: number = 100): Promise<AuditLog[]> {
    const response = await this.client.get('/audit/logs/my', { params: { limit } });
    return response.data.logs || [];
  }

  /**
   * Get audit logs by user ID
   */
  async getAuditLogsByUser(userId: number, limit: number = 100): Promise<AuditLog[]> {
    const response = await this.client.get(`/audit/logs/user/${userId}`, { params: { limit } });
    return response.data.logs || [];
  }

  /**
   * Get audit logs by resource
   */
  async getAuditLogsByResource(
    resource: string,
    resourceId?: string,
    limit: number = 100
  ): Promise<AuditLog[]> {
    const params: any = { limit };
    if (resourceId) params.resourceId = resourceId;
    const response = await this.client.get(`/audit/logs/resource/${resource}`, { params });
    return response.data.logs || [];
  }

  /**
   * Get audit statistics
   */
  async getAuditStatistics(days: number = 30): Promise<AuditStatistics> {
    const response = await this.client.get('/audit/statistics', { params: { days } });
    return response.data;
  }

  /**
   * Export audit logs
   */
  async exportAuditLogs(
    filters: Omit<AuditLogFilters, 'page' | 'limit'>,
    format: 'csv' | 'json' = 'csv'
  ): Promise<AuditExportJob> {
    const response = await this.client.post('/audit/export', { ...filters, format });
    return response.data;
  }

  /**
   * Get export job status
   */
  async getExportJobStatus(jobId: string): Promise<AuditExportJob> {
    const response = await this.client.get(`/audit/export/${jobId}`);
    return response.data;
  }

  /**
   * Get retention policies
   */
  async getRetentionPolicies(): Promise<RetentionPolicy[]> {
    const response = await this.client.get('/audit/retention');
    return response.data;
  }

  /**
   * Update retention policy
   */
  async updateRetentionPolicy(
    entity_type: string,
    retention_days: number,
    description?: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await this.client.put('/audit/retention', {
      entity_type,
      retention_days,
      description,
    });
    return response.data;
  }

  /**
   * Run cleanup job
   */
  async runCleanupJob(
    entity_type: string,
    retention_days: number
  ): Promise<AuditCleanupJob> {
    const response = await this.client.post('/audit/cleanup', {
      entity_type,
      retention_days,
    });
    return response.data;
  }

  /**
   * Get cleanup job status
   */
  async getCleanupJobStatus(jobId: string): Promise<AuditCleanupJob> {
    const response = await this.client.get(`/audit/cleanup/${jobId}`);
    return response.data;
  }

  // ============================================
  // AUDITOR-SPECIFIC METHODS
  // ============================================

  /**
   * Get companies assigned to the auditor
   */
  async getAssignedCompanies(): Promise<AuditorCompany[]> {
    const response = await this.client.get('/audit/companies');
    return response.data.data || [];
  }

  /**
   * Get frameworks for review by the auditor
   */
  async getFrameworksForReview(): Promise<FrameworkReview[]> {
    const response = await this.client.get('/audit/frameworks');
    return response.data.data || [];
  }

  /**
   * Get evidence for review by the auditor
   */
  async getEvidenceForReview(): Promise<EvidenceReview[]> {
    const response = await this.client.get('/audit/evidence');
    return response.data.data || [];
  }

  /**
   * Get audit findings for the auditor
   */
  async getAuditFindings(): Promise<AuditFinding[]> {
    const response = await this.client.get('/audit/findings');
    return response.data.data || [];
  }

  /**
   * Create a new audit finding
   */
  async createAuditFinding(finding: Partial<AuditFinding>): Promise<AuditFinding> {
    const response = await this.client.post('/audit/findings', finding);
    return response.data.data;
  }

  /**
   * Update an audit finding
   */
  async updateAuditFinding(id: number, finding: Partial<AuditFinding>): Promise<AuditFinding> {
    const response = await this.client.put(`/audit/findings/${id}`, finding);
    return response.data.data;
  }

  /**
   * Get assessment summaries for the auditor
   */
  async getAssessmentSummaries(): Promise<AssessmentSummary[]> {
    const response = await this.client.get('/audit/assessments');
    return response.data.data || [];
  }

  /**
   * Generate assessment report
   */
  async generateAssessmentReport(assessmentId: number, format: 'pdf' | 'excel' | 'word' = 'pdf'): Promise<{ downloadUrl: string }> {
    const response = await this.client.post(`/audit/assessments/${assessmentId}/report`, { format });
    return response.data;
  }
}

export const auditApi = new AuditApiClient();
export default auditApi;
